import { FaRegHeart } from 'react-icons/fa';
import { memo, useEffect, useState, type JSX } from 'react';
import { formatIndianPrice } from '@/utils/formatting';
import {
  buildOfferBannerData,
  computeOfferPrices,
} from './brandOfferTilePreview';
import {
  OFFER_BANNER_COPY,
  OFFER_PREVIEW_BASE_PRODUCT,
  OFFER_PREVIEW_FALLBACK_IMAGE,
  OFFER_PREVIEW_RATING_BADGE,
} from './OfferProductPreview.constants';
import styles from './OfferProductPreview.module.css';
import type {
  OfferBannerNonEmpty,
  OfferBannerProps,
  OfferProductPreviewProps,
  SalePillVariant,
} from './OfferProductPreview.types';
import {
  getSalePillState,
  marketplaceLogoForBrand,
  offerBannerNeedsLiveClock,
} from './OfferProductPreview.utils';

function salePillClassForVariant(variant: SalePillVariant): string {
  switch (variant) {
    case 'muted':
      return styles.salePillMuted;
    case 'ended':
      return styles.salePillEnded;
    case 'live':
      return styles.salePillLive;
    default: {
      const _exhaustive: never = variant;
      return _exhaustive;
    }
  }
}

function renderOfferBannerContent(data: OfferBannerNonEmpty): JSX.Element {
  switch (data.kind) {
    case 'BEST_PRICE':
      return (
        <>
          <span className={styles.bannerPrice}>
            <span className={styles.bannerPrefix}>
              {OFFER_BANNER_COPY.getAtPrefix}
            </span>
            ₹{formatIndianPrice(data.bestPrice)}
          </span>
          <span className={styles.bannerCaption}>
            {OFFER_BANNER_COPY.bestPriceCaption}
          </span>
        </>
      );

    case 'PRICE':
      return (
        <>
          <span className={styles.bannerPrice}>
            {data.prefix ? (
              <span className={styles.bannerPrefix}>{data.prefix}</span>
            ) : null}
            ₹{formatIndianPrice(data.price)}
            {data.inlineSuffix ? (
              <span className={styles.bannerInlineSuffix}>
                {data.inlineSuffix}
              </span>
            ) : null}
          </span>
          {data.caption ? (
            <span
              className={`${styles.bannerCaption} ${styles.bannerCaptionEnd}`}
            >
              {data.caption}
            </span>
          ) : null}
        </>
      );

    case 'PRICE_TIMER':
      return (
        <>
          <span className={styles.bannerPrice}>
            {data.prefix ? (
              <span className={styles.bannerPrefix}>{data.prefix}</span>
            ) : null}
            ₹{formatIndianPrice(data.price)}
            {data.inlineSuffix ? (
              <span className={styles.bannerInlineSuffix}>
                {data.inlineSuffix}
              </span>
            ) : null}
          </span>
          <span
            className={`${styles.bannerCountdown} ${styles.bannerCountdownEnd}`}
          >
            {data.countdown}
          </span>
          {data.caption ? (
            <span
              className={`${styles.bannerCaption} ${styles.bannerCaptionEnd}`}
            >
              {data.caption}
            </span>
          ) : null}
        </>
      );

    case 'TIMER':
      return (
        <span className={styles.bannerCaptionRow}>
          <span className={styles.bannerCaption}>{data.caption} -</span>
          <span className={styles.bannerCountdown}>{data.countdown}</span>
        </span>
      );

    case 'CAPTION_SUFFIX':
      return (
        <span className={styles.bannerCaptionRow}>
          <span className={styles.bannerCaption}>{data.caption} - </span>
          <span className={styles.bannerCountdown}>{data.suffix}</span>
        </span>
      );

    case 'TEXT_ONLY':
      return (
        <span className={`${styles.bannerCaption} ${styles.bannerTextOnly}`}>
          {data.caption}
        </span>
      );

    default: {
      const _exhaustive: never = data;
      return _exhaustive;
    }
  }
}

function OfferBanner({ data }: OfferBannerProps): JSX.Element | null {
  if (data.kind === 'NONE') return null;
  return (
    <div className={styles.offerBanner}>{renderOfferBannerContent(data)}</div>
  );
}

function OfferProductPreview({
  input,
  compact = false,
}: OfferProductPreviewProps): JSX.Element {
  const [now, setNow] = useState(() => new Date());

  const prices = computeOfferPrices(input.offerType, input.discountConfig);
  const pill = getSalePillState(input, now);
  const banner = buildOfferBannerData(input, prices, now);
  const needsLiveClock = offerBannerNeedsLiveClock(banner);

  useEffect(() => {
    if (!needsLiveClock) return;
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, [needsLiveClock]);

  const logo = marketplaceLogoForBrand(input.brandName);
  const imgSrc = input.imageUrl ?? OFFER_PREVIEW_FALLBACK_IMAGE;

  const baseMrp = OFFER_PREVIEW_BASE_PRODUCT.price;
  const baseSelling = OFFER_PREVIEW_BASE_PRODUCT.discountPrice;
  const baseShowStrike = baseSelling < baseMrp;
  const basePercentOff = baseShowStrike
    ? Math.round(((baseMrp - baseSelling) / baseMrp) * 100)
    : null;
  const baseMrpFmt = formatIndianPrice(baseMrp);
  const baseSellFmt = formatIndianPrice(baseSelling);

  return (
    <div className={`${styles.root} ${compact ? styles.rootCompact : ''}`}>
      <div className={styles.imageBlock}>
        <img className={styles.heroImg} src={imgSrc} alt="" />
        {pill && (
          <div
            className={`${styles.salePill} ${salePillClassForVariant(pill.variant)}`}
          >
            {pill.showDot && <span className={styles.salePillDot} />}
            {pill.label}
          </div>
        )}
        <div className={styles.wishWrap}>
          <span className={styles.wishBtn} title="Wishlist">
            <FaRegHeart size={16} />
          </span>
        </div>
        <div className={styles.brandDot}>
          {logo ? (
            <img src={logo} alt="" />
          ) : (
            <span className={styles.brandDotFallback}>
              {input.brandName.slice(0, 3).toUpperCase()}
            </span>
          )}
        </div>
        <div className={styles.ratingBadge}>{OFFER_PREVIEW_RATING_BADGE}</div>
      </div>
      <div className={styles.body}>
        <p className={styles.titleRow}>
          <span className={styles.brand}>{input.brandName}</span>{' '}
          <span className={styles.titleRest}>{input.description}</span>
        </p>
        <div className={styles.priceRow}>
          <span className={styles.price}>₹{baseSellFmt}</span>
          {baseShowStrike && (
            <>
              <span className={styles.mrp}>₹{baseMrpFmt}</span>
              {basePercentOff != null && (
                <span className={styles.pctOff}>{basePercentOff}% off</span>
              )}
            </>
          )}
        </div>
        <OfferBanner data={banner} />
      </div>
    </div>
  );
}

export default memo(OfferProductPreview);
